import React, { useState } from "react";
import {
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Box,
  CircularProgress,
} from "@mui/material";
import { Formik, Form, Field } from "formik";
import { toast } from "react-hot-toast";
import useStudentEvaluationService from "../../api/services/StudentEvaluationService";
import * as yup from "yup";

// Validation schema
const EditEvaluationSchema = yup.object().shape({
  spiritualLife: yup.string().oneOf(["A", "B", "F", ""], "Please select A, B, or F"),
  academicLife: yup.string().oneOf(["A", "B", "F", ""], "Please select A, B, or F"),
  behaviour: yup.string().oneOf(["A", "B", "F", ""], "Please select A, B, or F"),
  manualWork: yup.string().oneOf(["A", "B", "F", ""], "Please select A, B, or F"),
  health: yup.string().oneOf(["A", "B", "F", ""], "Please select A, B, or F"),
  leadershipSkills: yup.string().oneOf(["A", "B", "F", ""], "Please select A, B, or F"),
  sports: yup.string().oneOf(["A", "B", "F", ""], "Please select A, B, or F"),
});

const EditStudentEvaluation = ({ open, onClose, onSuccess, editingData }) => {
  const { updatePartialEvaluationByTermStudent } = useStudentEvaluationService();
  const [isLoading, setIsLoading] = useState(false);

  const getInitialValues = () => {
    if (!editingData) {
      return {
        spiritualLife: "",
        academicLife: "",
        behaviour: "",
        manualWork: "",
        health: "",
        leadershipSkills: "",
        sports: "",
      };
    }

    // Get data from originalData if available, otherwise from editingData directly
    const data = editingData.originalData || editingData;

    return {
      spiritualLife: data.spiritualLife || "",
      academicLife: data.academicLife || "",
      behaviour: data.behaviour || "",
      manualWork: data.manualWork || "",
      health: data.health || "",
      leadershipSkills: data.leadershipSkills || "",
      sports: data.sports || "",
    };
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    if (!editingData) {
      toast.error("No data to update");
      setSubmitting(false);
      return;
    }

    try {
      setIsLoading(true);
      const originalData = editingData.originalData || editingData;
      
      // Use the original termId from the selected row (fixed, not editable)
      const termId = originalData.termId || originalData.term?.id;
      const studentId = originalData.studentId || originalData.student?.id;

      if (!termId || !studentId) {
        toast.error("Term and Student information is required");
        setSubmitting(false);
        setIsLoading(false);
        return;
      }

      // Prepare DTO - include all fields, even if empty
      const dto = {
        spiritualLife: values.spiritualLife || null,
        academicLife: values.academicLife || null,
        behaviour: values.behaviour || null,
        manualWork: values.manualWork || null,
        health: values.health || null,
        leadershipSkills: values.leadershipSkills || null,
        sports: values.sports || null,
      };

      // Use partial update to only update provided fields
      await updatePartialEvaluationByTermStudent(termId, studentId, dto, () => {
        toast.success("Student evaluation updated successfully");
        onSuccess?.();
        onClose();
      });
    } catch (error) {
      console.error("Error updating student evaluation:", error);
      toast.error("Failed to update student evaluation: " + (error.response?.data?.message || error.message || "Unknown error"));
    } finally {
      setSubmitting(false);
      setIsLoading(false);
    }
  };

  if (!editingData) {
    return null;
  }

  // Get term name and student name for display
  const originalData = editingData.originalData || editingData;
  const termName = originalData.term?.termName || editingData.termName || "N/A";
  const studentName = originalData.student?.studentName || editingData.studentName || "N/A";

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Edit Student Evaluation
        <div style={{ fontSize: '14px', fontWeight: 'normal', marginTop: '8px', color: '#666' }}>
          Term: <strong>{termName}</strong> | Student: <strong>{studentName}</strong>
        </div>
      </DialogTitle>
      <DialogContent>
        <Formik
          initialValues={getInitialValues()}
          validationSchema={EditEvaluationSchema}
          onSubmit={handleSubmit}
          enableReinitialize={true}
        >
          {({ values, errors, touched, isSubmitting }) => (
            <Form>
              <Grid container spacing={2} mt={1}>
                {/* Grade Fields */}
                {[
                  { field: "spiritualLife", label: "Spiritual Life" },
                  { field: "academicLife", label: "Academic Life" },
                  { field: "behaviour", label: "Behaviour" },
                  { field: "manualWork", label: "Manual Work" },
                  { field: "health", label: "Health" },
                  { field: "leadershipSkills", label: "Leadership Skills" },
                  { field: "sports", label: "Sports" },
                ].map(({ field, label }) => (
                  <Grid item xs={12} lg={6} key={field}>
                    <Field
                      as={TextField}
                      select
                      fullWidth
                      label={label}
                      name={field}
                      variant="filled"
                      value={values[field] || ""}
                      error={touched[field] && !!errors[field]}
                      helperText={touched[field] && errors[field]}
                    >
                      <MenuItem value="">None</MenuItem>
                      <MenuItem value="A">A</MenuItem>
                      <MenuItem value="B">B</MenuItem>
                      <MenuItem value="F">F</MenuItem>
                    </Field>
                  </Grid>
                ))}
              </Grid>

              <DialogActions sx={{ mt: 2 }}>
                <Button onClick={onClose} color="secondary" disabled={isSubmitting || isLoading}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="secondary"
                  disabled={isSubmitting || isLoading}
                >
                  {isSubmitting || isLoading ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={16} />
                      Updating...
                    </Box>
                  ) : (
                    "Update"
                  )}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};

export default EditStudentEvaluation;

